import { forwardRef } from 'react'

import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'
import { motion } from 'motion/react'
import { tv, type VariantProps } from 'tailwind-variants'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import { Text, type TypographyVariant } from '../../atoms/text/text'

import { AnimatedTableCheckboxIcon } from './animated-table-checkbox-icon'
import { DefaultCheckboxIcons } from './default-checkbox-icons'
import { TableCheckboxIcons } from './table-checkbox-icons'

const checkboxVariants = tv({
  base: [
    'group peer inline-flex shrink-0 items-center justify-center',
    'cursor-pointer transition-colors',
    'text-surface-title-50',
    'focus-visible:ring-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'data-[disabled]:text-surface-body data-[disabled]:cursor-not-allowed'
  ],
  variants: {
    size: {
      sm: 'text-[18px]',
      md: 'text-[21px]',
      lg: 'text-[27px]'
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

const labelSizeVariant = {
  sm: 'caption-lg',
  md: 'body-md',
  lg: 'body-xl'
} as const satisfies Record<string, TypographyVariant>

export type CheckboxProps = React.ComponentProps<
  typeof CheckboxPrimitive.Root
> &
  VariantProps<typeof checkboxVariants> & {
    className?: string
    label?: string
    appearance?: 'default' | 'table'
    /** Animates table artwork. Reduced motion, disabled and read-only states skip it. */
    animated?: boolean
  }

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      size = 'md',
      className,
      label,
      appearance = 'default',
      animated = true,
      ...props
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion()
    const shouldAnimate =
      animated && !reducedMotion && !props.disabled && !props.readOnly
    const checkbox = (
      <CheckboxPrimitive.Root
        ref={ref}
        data-slot='checkbox'
        data-appearance={appearance}
        className={cn(
          checkboxVariants({ size }),
          appearance === 'table' &&
            'text-surface-body data-[checked]:text-primary data-[indeterminate]:text-primary size-6',
          className
        )}
        {...props}
      >
        {appearance === 'table' ? (
          shouldAnimate ? (
            <CheckboxPrimitive.Indicator
              keepMounted
              render={(indicatorProps, state) => (
                <span {...indicatorProps} className='inline-flex size-6'>
                  {state.disabled || state.readOnly ? (
                    <TableCheckboxIcons />
                  ) : (
                    <motion.span
                      className='inline-flex size-6'
                      tabIndex={-1}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 30,
                        mass: 0.6
                      }}
                    >
                      <AnimatedTableCheckboxIcon
                        checked={state.checked}
                        indeterminate={state.indeterminate}
                      />
                    </motion.span>
                  )}
                </span>
              )}
            />
          ) : (
            <TableCheckboxIcons />
          )
        ) : (
          <DefaultCheckboxIcons />
        )}
      </CheckboxPrimitive.Root>
    )

    if (!label) return checkbox

    return (
      <label className='inline-flex cursor-pointer items-center gap-2 has-[[data-disabled]]:cursor-not-allowed'>
        {checkbox}
        <Text
          as='span'
          variant={labelSizeVariant[size ?? 'md']}
          data-text={label}
          className={cn(
            'inline-flex select-none flex-col',
            'text-surface-title-50',
            'peer-data-[checked]:font-semibold peer-data-[indeterminate]:font-semibold',
            'peer-data-[disabled]:text-surface-body',
            'after:invisible after:block after:h-0 after:overflow-hidden after:font-semibold after:content-[attr(data-text)]'
          )}
        >
          {label}
        </Text>
      </label>
    )
  }
)
Checkbox.displayName = 'Checkbox'
